import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Maximize, Minimize } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface SlideContent {
  title: string;
  content: React.ReactNode;
  id?: number;
}

interface SlideshowProps {
  slides: SlideContent[];
}

/**
 * A reusable slideshow component for lectures with fullscreen capability
 */
const Slideshow: React.FC<SlideshowProps> = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      // Enter fullscreen using the Browser Fullscreen API
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
      }
    } else {
      // Exit fullscreen using the Browser Fullscreen API
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
      }
    }
  };
  
  // Add event listeners for fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFullscreen) {
        if (e.key === 'ArrowRight') nextSlide();
        else if (e.key === 'ArrowLeft') prevSlide();
        else if (e.key === 'Escape') setIsFullscreen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  return (
    <div 
      ref={slideshowRef}
      className={`mb-8 transition-all duration-300 ${
        isFullscreen 
          ? 'fixed inset-0 z-50 bg-white overflow-y-auto' 
          : 'relative rounded-lg border border-gray-200 overflow-hidden'
      }`}
    >
      {/* Slide content */}
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">{slides[currentSlide].title}</h2>
        <div className="prose max-w-none">
          {slides[currentSlide].content}
        </div>
      </div>
      
      {/* Controls - fixed at bottom when in fullscreen */}
      <div className={`flex items-center justify-between bg-gray-50 px-6 py-3 ${
        isFullscreen ? 'sticky bottom-0 left-0 right-0' : ''
      }`}>
        <div className="flex items-center gap-2">
          <Button 
            onClick={prevSlide}
            variant="outline"
            size="sm"
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button 
            onClick={nextSlide}
            variant="outline"
            size="sm"
            className="gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="text-sm text-gray-500">
          Slide {currentSlide + 1} of {slides.length}
        </div>
        
        <Button 
          onClick={toggleFullscreen}
          variant="default"
          size="sm"
          className="gap-1"
        >
          {isFullscreen ? (
            <>
              <Minimize className="h-4 w-4" />
              Exit Fullscreen
            </>
          ) : (
            <>
              <Maximize className="h-4 w-4" />
              View Fullscreen
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Slideshow;